(ns bleene.dag.events
  (:require
   ["elkjs/lib/elk-api.js" :as ELK-API]
   [applied-science.js-interop :as jsi]
   [bleene.dag.subs :refer [group-rect]]
   [bleene.utils :as utils]
   [clojure.edn :as edn]
   [minorodata.utils :as minoro.utils]
   [re-frame.core :as re-frame]
   [re-posh.core :as re-posh]))

;; ELK docs https://www.eclipse.org/elk/reference.html
;; Ports:
;; https://www.eclipse.org/elk/documentation/tooldevelopers/graphdatastructure/jsonformat.html


(defn new-dag-state
  []
  {:scale           1
   :offset-x        0
   :offset-y        0
   :x               0
   :y               0
   :navigate-to     nil
   :connected-nodes #{}
   :connected-edges #{}
   :nodes           []
   :edges           []
   :groups          ["root"]
   :selected-node   nil
   :selected-edge   nil
   :selected-group  nil
   :temp-connection nil
   :drawing-edge?   false
   :listen-events?  true
   :show-sources?   @(re-posh/subscribe [:unleash/is-enabled "fe.manual-dag-layout"])})


(re-frame/reg-event-db
 :dag/create-new-dag
 (fn [db [_ dag-id]]
   (assoc-in db [:dag dag-id] (new-dag-state))))


(defn delete-element [collection id]
  (let [position (jsi/call collection :indexOf id)]
    (if (>= position 0)
      (into (subvec collection 0 position)
            (subvec collection (inc position)))
      collection)))


(re-frame/reg-event-db
 :dag/set-offset
 (fn [db [_ dag-id {:keys [offset-x offset-y]}]]
   (update-in db [:dag dag-id] assoc
     :offset-x offset-x
     :offset-y offset-y)))


(re-frame/reg-event-db
 :dag/set-loading
 (fn [db [_ loading? & [message]]]
   (if loading?
     (assoc db :loader {:loading? loading?
                        :message  message})
     (assoc db :loader {:loading? false}))))


(re-frame/reg-event-db
 :dag/set-scale
 (fn [db [_ dag-id scale]]
   (update-in db [:dag dag-id] assoc :scale scale)))


(re-frame/reg-event-db
 :dag/set-position
 (fn [db [_ dag-id {:keys [x y]}]]
   (update-in db [:dag dag-id] assoc :x x :y y)))

(re-frame/reg-event-db
 :dag/navigate-to-node
 (fn [db [_ dag-id node-id]]
   (let [{:keys [x y]} (get-in db [:jobs node-id])]
     (update-in db [:dag dag-id] assoc :navigate-to {:x x :y y}))))


(re-frame/reg-event-db
 :dag/reset-navigation
 (fn [db [_ dag-id]]
   (update-in db [:dag dag-id] assoc :navigate-to nil)))


(re-frame/reg-event-db
 :dag/open-transform-delete-modal
 (fn [db [_ open? metadata]]
   (update db :transform-delete-modal assoc :open? open? :metadata metadata)))

(re-frame/reg-event-db
 :dag/open-extract-delete-modal
 (fn [db [_ open? metadata]]
   (update db :extract-delete-modal assoc :open? open? :metadata metadata)))


(re-frame/reg-event-db
 :dag/open-context-menu
 (fn [db [_ open?]]
   (update db :context-menu assoc :open? open?)))


(re-frame/reg-event-db
 :dag/context-menu-target
 (fn [db [_ target]]
   (update db :context-menu assoc :target target)))


(re-frame/reg-event-db
 :dag/add-node
 (fn [db [_ dag-id position]]
   (let [{:keys [x y]} position
         {:keys [node-width node-height]} (get db :dag-params)
         id   (str (random-uuid))
         node {:id     id
               :name   nil
               :x      (- x (/ node-width 2))
               :y      (- y (/ node-height 2))
               :type   "transform"
               :status :default}]
     (-> db
         (assoc-in [:jobs id] node)
         (assoc-in [:dag dag-id :selected-node] id)
         (update-in [:dag dag-id :nodes] conj id)))))


(re-frame/reg-event-fx
 :dag/save-node
 (fn [{:keys [db]} [_ dag-id id props]]
   {:db (-> db
            (update-in [:jobs id] merge props)
            (update-in [:groups (:group-id props) :nodes] conj id)
            (assoc-in [:dag dag-id :selected-node] nil))
    :fx [[:dispatch [:dag/create-transform dag-id (assoc props :id id)]]]}))


(re-frame/reg-event-db
 :dag/set-node-position
 (fn [db [_ id position]]
   (update-in db [:jobs id] merge position)))


(defn update-connected-edges-position [edges node-id {:keys [x y]} dag-params]
  (let [{:keys [node-height node-width]} dag-params
        left  {:x x
               :y (+ y (/ node-height 2))}
        right {:x (+ x node-width)
               :y (+ y (/ node-height 2))}]

    (into {}
          (map (fn [[edge-id edge]]
                 [edge-id
                  (cond-> edge
                    (= node-id (:start-id edge))
                    (assoc :start right)

                    (= node-id (:end-id edge))
                    (assoc :end left))])
               edges))))


(defn update-connected-edges-position-2 [edges nodes dag-params]
  (let [{:keys [node-height node-width]} dag-params]
    (into {}
          (map (fn [[edge-id edge]]
                 (let [start-node (get nodes (:start-id edge))
                       end-node   (get nodes (:end-id edge))
                       left  {:x (:x end-node)
                              :y (+ (:y end-node) (/ node-height 2))}
                       right {:x (+ (:x start-node) node-width)
                              :y (+ (:y start-node) (/ node-height 2))}]
                   [edge-id
                    (cond-> edge
                      (some? start-node)
                      (assoc :start right)

                      (some? end-node)
                      (assoc :end left))]))
               edges))))


(re-frame/reg-event-db
 :dag/move-node-connections
 (fn [db [_ node-id position]]
   (let [edges (update-connected-edges-position
                (:connections db)
                node-id
                position
                (:dag-params db))]
     (assoc db :connections edges))))


(re-frame/reg-event-db
 :dag/select-node
 (fn [db [_ dag-id node-id]]
   (if (some? node-id)
     (let [connections (get db :connections)
           {:keys [nodes edges]} (reduce (fn [acc {:keys [start-id end-id id]}]
                                           (cond-> acc
                                                   (= node-id start-id)
                                                   (update :nodes conj end-id)

                                                   (= node-id end-id)
                                                   (update :nodes conj start-id)

                                                   (or (= node-id start-id)
                                                       (= node-id end-id))
                                                   (update :edges conj id)))
                                         {:nodes #{}
                                          :edges #{}}
                                         (vals connections))]
       (update-in db [:dag dag-id] assoc :selected-node node-id
                                         :connected-nodes nodes
                                         :connected-edges edges))

     (update-in db [:dag dag-id] assoc :selected-node nil
                                       :connected-nodes #{}
                                       :connected-edges #{}))))


(re-frame/reg-event-db
 :dag/select-edge
 (fn [db [_ dag-id id]]
   (update-in db [:dag dag-id] assoc :selected-edge id)))


(re-frame/reg-event-db
 :dag/add-temp-edge
 (fn [db [_ dag-id id position]]
   (let [{:keys [x y]} (get-in db [:jobs id])
         position' {:x (+ x (:x position))
                    :y (+ y (:y position))}
         temp-edge {:start    position'
                    :end      position'
                    :start-id id
                    :type     :temp}]
     (-> db
         (assoc-in [:dag dag-id :temp-connection] temp-edge)
         (assoc-in [:dag dag-id :drawing-edge?] true)))))


(re-frame/reg-event-db
 :dag/update-temp-edge
 (fn [db [_ dag-id position]]
   (assoc-in db [:dag dag-id :temp-connection :end] position)))


(re-frame/reg-event-db
 :dag/reset-temp-edge
 (fn [db [_ dag-id]]
   (when-let [temp-edge (get-in db [:dag dag-id :temp-connection])]
     (let [selected-node (get-in db [:dag dag-id :selected-node])
           input-node    (get temp-edge :start-id)
           {:keys [node-height]} (:dag-params db)
           new-edge-id   (str (:start-id temp-edge) ":" selected-node)
           {:keys [x y]} (get-in db [:jobs selected-node])
           position      {:x x
                          :y (+ y (/ node-height 2))}
           temp-edge'    (assoc temp-edge
                           :end-id selected-node
                           :id new-edge-id
                           :end position
                           :type :default)]
       (if (or (nil? selected-node)
               (some? (get-in db [:connections new-edge-id]))
               (= selected-node (:start-id temp-edge)))
         (-> db
             ;; don't add connection
             (update-in [:dag dag-id] assoc :drawing-edge? nil
                                            :temp-connection nil))

         (-> db
             (update :connections assoc new-edge-id temp-edge')
             (update-in [:jobs selected-node :input] conj new-edge-id)
             (update-in [:jobs input-node :output] conj new-edge-id)
             (update-in [:dag dag-id :edges] conj new-edge-id)
             (update-in [:dag dag-id] assoc :drawing-edge? nil
                                            :temp-connection nil
                                            :selected-node nil)))))))


(defn delete-connections [connections ids]
  (apply dissoc connections ids))


(defn delete-items [items ids]
  (let [deleted-items (set ids)]
    (filter #(not (contains? deleted-items %)) items)))


(defn delete-node-with-edges [db dag-id node-id]
  (let [{:keys [input output group-id]} (get-in db [:jobs node-id])
        connections (concat input output)]
    (-> db
        (update :jobs dissoc node-id)
        (update :connections delete-connections connections)
        (update-in [:groups group-id :nodes] delete-items [node-id])
        (update-in [:dag dag-id :nodes] delete-element node-id)
        (update-in [:dag dag-id :edges] delete-items connections)
        (assoc-in [:dag dag-id :selected-node] nil))))


(re-frame/reg-event-db
 :dag/delete-elements
 (fn [db [_ dag-id]]
   (let [selected-node (get-in db [:dag dag-id :selected-node])
         selected-edge (get-in db [:dag dag-id :selected-edge])]
     (cond-> db
             (some? selected-node) (delete-node-with-edges dag-id selected-node)
             (some? selected-node) (update :connections dissoc selected-edge)))))


(re-frame/reg-event-db
 :dag/delete-node
 (fn [db [_ dag-id id]]
   (delete-node-with-edges db dag-id id)))


(defn update-nodes-position [nodes delta-x delta-y]
  (->> nodes
       (map (fn [[node-id node]]
              (let [{:keys [x y]} node
                    new-x (+ x delta-x)
                    new-y (+ y delta-y)]
                [node-id (assoc node :x new-x :y new-y)])))
       (into {})))


(re-frame/reg-event-db
 :dag/move-group-nodes
 (fn [db [_ group-id delta-x delta-y]]
   (let [nodes-ids  (get-in db [:groups group-id :nodes])
         jobs       (get db :jobs)
         dag-params (get db :dag-params)
         nodes      (select-keys jobs nodes-ids)
         nodes'     (update-nodes-position nodes delta-x delta-y)
         edges      (update-connected-edges-position-2
                     (:connections db)
                     nodes'
                     dag-params)]
     (-> db
         (update :jobs merge nodes')
         (assoc :connections edges)))))


(re-frame/reg-event-db
 :dag/select-group
 (fn [db [_ dag-id group-id]]
   (assoc-in db [:dag dag-id :selected-group] group-id)))


(re-frame/reg-event-db
 :dag/update-jobs
 (fn [db [_ jobs]]
   (update db :jobs merge jobs)))


(re-frame/reg-event-db
 :dag/update-raw-groups
 (fn [db [_ groups]]
   (let [groups-map (into {} (map (juxt :id identity) groups))]
     (update db :raw-groups merge groups-map))))


(defn swap-nodes [nodes old-id id]
  (let [idx (.indexOf nodes old-id)]
    (if (>= idx 0)
      (assoc nodes idx id)
      nodes)))


(re-frame/reg-event-db
 :dag/update-job
 (fn [db [_ dag-id temp-id {:keys [id group-id]}]]
   (let [old-job (get-in db [:jobs temp-id])
         new-job (assoc old-job :id id)]
     (-> db
         (update :jobs dissoc temp-id)
         (assoc-in [:jobs id] new-job)
         (update-in [:groups group-id :nodes] swap-nodes temp-id id)
         (update-in [:dag dag-id :nodes] swap-nodes temp-id id)))))


(re-frame/reg-event-db
 :dag/mute-events
 (fn [db [_ dag-id mute?]]
   (assoc-in db [:dag dag-id :listen-events?] (not mute?))))


(defn find-downstream [output jobs connections]
  (loop [edges  output
         result {:edges [] :nodes []}]
    (let [nodes       (map (fn [edge]
                             (get-in connections [edge :end-id]))
                           edges)
          next-output (reduce (fn [acc node]
                                (let [output (get-in jobs [node :output])]
                                  (concat acc output)))
                              []
                              nodes)
          result'     (-> result
                          (update :nodes concat nodes)
                          (update :edges concat edges))

          in-cycle?   (= (set (:nodes result')) (set (:nodes result)))]

      (cond in-cycle? result'
            (seq next-output) (recur next-output result')
            :otherwise result'))))


(defn find-upstream [input jobs all-edges]
  (loop [edges  input
         result {:edges [] :nodes []}]
    (let [nodes       (map (fn [edge]
                             (get-in all-edges [edge :start-id]))
                           edges)
          next-output (reduce (fn [acc node]
                                (let [input (get-in jobs [node :input])]
                                  (concat acc input)))
                              []
                              nodes)

          result'     (-> result
                          (update :nodes concat nodes)
                          (update :edges concat edges))

          in-cycle?   (= (set (:nodes result')) (set (:nodes result)))]

      (cond in-cycle? result'
            (seq next-output) (recur next-output result')
            :otherwise result'))))


(defn job-in-group? [job group-id]
  (= group-id
     (get-in job [:transform :group-id])))

(defn job-has-any-of-tags? [job tags]
  (not (empty? (clojure.set/intersection
                (set tags)
                (set (get-in job [:transform :tags]))))))


(defn filter-jobs-by-group-id [jobs group-id]
  (select-keys jobs (for [[job-id job] jobs :when (job-in-group? job group-id)] job-id)))

(defn filter-jobs-by-tags [jobs tags]
  (select-keys jobs (for [[job-id job] jobs :when (job-has-any-of-tags? job tags)] job-id)))

(def empty-dir-filter "all")
(def empty-tags-filter #{})

(re-frame/reg-event-fx
 :dag/filter-nodes
 (fn [{:keys [db]} [_ dag-id node-id]]
   (let [node-id  (or node-id
                      (get-in db [:dag dag-id :filters :node]))

         {:keys [input output]} (get-in db [:jobs node-id])

         connections  (get db :connections)
         jobs         (get db :jobs)

         direction    (get-in db [:dag dag-id :filters :dir] empty-dir-filter)
         group-id     (get-in db [:dag dag-id :filters :group] nil)
         tags         (get-in db [:dag dag-id :filters :tags] empty-tags-filter)

         has-filters? (or (not= direction empty-dir-filter)
                          group-id
                          (not= tags empty-tags-filter))

         jobs         (cond-> jobs
                        group-id         (filter-jobs-by-group-id group-id)
                        (not-empty tags) (filter-jobs-by-tags tags))

         downstream   (when node-id
                        (find-downstream output jobs connections))
         upstream     (when node-id
                        (find-upstream input jobs connections))


         nodes        (if node-id
                        (case direction
                          "upstream" (concat (:nodes upstream) [node-id])
                          "downstream" (concat [node-id] (:nodes downstream))
                          (concat (:nodes upstream) [node-id] (:nodes downstream)))
                        (keys jobs))

         unique-nodes (distinct nodes)

         edges        (if node-id
                        (case direction
                          "upstream" (:edges upstream)
                          "downstream" (:edges downstream)
                          (concat (:edges upstream) (:edges downstream)))
                        (keys connections))

         unique-edges (distinct edges)

         groups'      (->> unique-nodes
                           (select-keys jobs)
                           vals
                           (group-by :group-id)
                           keys)
         backup       (get-in db [:dag dag-id :backup])
         backup-state (if (some? backup)
                        backup
                        {:connections connections
                         :jobs        jobs})

         node (get-in db [:jobs node-id])]

     {:db (-> db
              (update-in [:dag dag-id] assoc :nodes unique-nodes
                                             :edges unique-edges
                                             :groups groups')
              (update-in [:dag dag-id :filters] assoc :node node-id
                                                      :dir direction
                                                      :group group-id)
              (update-in [:dag dag-id] assoc :backup backup-state)

              (update-in [:tabs dag-id] assoc :name (if (some? node)
                                                     (str (:name node) " " direction)
                                                     "All nodes")))

      :fx [[:dispatch [(if has-filters? :dag/full-autolayout :dag/full-layout)
                       dag-id {:callback (cond
                                           node-id [:dag/navigate-to-node dag-id node-id]
                                           group-id [:dag/navigate-to-node dag-id (first jobs)]
                                           :else nil)}]]]})))


(re-frame/reg-event-fx
 :dag/set-filter-dir
 (fn [{:keys [db]} [_ dag-id direction]]
   (let [node-in-filter (get-in db [:dag dag-id :filters :node])]
     (cond-> {:db (assoc-in db [:dag dag-id :filters :dir] direction)}
             (some? node-in-filter)
             (assoc :fx [[:dispatch [:dag/filter-nodes dag-id node-in-filter]]])))))


(re-frame/reg-event-fx
 :dag/set-filter-group
 (fn [{:keys [db]} [_ dag-id group-id]]
   (let [node-in-filter (get-in db [:dag dag-id :filters :node])]
     ;; Resets all other filters when group filter is engaged
     {:db (assoc-in db [:dag dag-id :filters] {:group group-id})
      :fx [[:dispatch [:dag/filter-nodes dag-id]]]})))


(re-frame/reg-event-fx
 :dag/set-filter-tags
 (fn [{:keys [db]} [_ dag-id tags]]
   (-> {:db (assoc-in db [:dag dag-id :filters :tags] tags)}
       (assoc :fx [[:dispatch [:dag/filter-nodes dag-id]]]))))


(re-frame/reg-event-fx
 :dag/set-sources-on-dag
 (fn [{:keys [db]} [_ dag-id enabled?]]

   (cond-> {:db (assoc-in db [:dag dag-id :show-sources?] enabled?)}
     (or (not= dag-id :default-dag)
         (not (:layout db)))
     (assoc :fx [[:dispatch [:dag/full-autolayout dag-id]]]))))

(re-frame/reg-event-fx
 :dag/set-show-all-extracts
 (fn [{:keys [db]} [_ dag-id show?]]

   (-> {:db (assoc-in db [:dag dag-id :show-all-extracts?] show?)}
     (assoc :fx [[:dispatch [:dag/convert-raw-data]]]))))

(re-frame/reg-event-fx
 :dag/clear-filter
 (fn [{:keys [db]} [_ dag-id]]
   (let [nodes   (keys (:jobs db))
         edges   (keys (:connections db))
         groups  (keys (:groups db))
         backup  (get-in db [:dag dag-id :backup])
         layout  (:layout db)
         node-id (get-in db [:dag dag-id :filters :node])]
     {:db (-> db
              (update-in [:dag dag-id]
                         assoc
                         :nodes nodes
                         :edges edges
                         :groups groups
                         :filters nil
                         :backup nil)
              (update-in [:tabs dag-id] assoc :name "All nodes")
              (update :jobs #(merge-with merge % (select-keys (:jobs layout) nodes) (:jobs backup)))
              (update :connections #(merge-with merge % (select-keys (:connections layout) edges) (:connections backup))))
      :fx [(when node-id [:dispatch [:dag/navigate-to-node dag-id node-id]])]})))


(re-frame/reg-event-fx
 :dag/cleanup-selected
 (fn [_ [_ dag-id]]
   {:fx [[:dispatch [:dag/select-node dag-id nil]]
         [:dispatch [:dag/select-edge dag-id nil]]
         [:dispatch [:dag/select-group dag-id nil]]]}))


(re-frame/reg-event-db
 :dag/update-layout-timestamp
 (fn [db [_ dag-id]]
   (let [ts (.getTime (js/Date.))]
     (assoc-in db [:dag dag-id :layout-timestamp] ts))))


(def elk
  (ELK-API. #js {:workerUrl  "/worker/elk-worker.min.js"
                 :algorithms #js ["layered"]}))


(def layout-options
  #js {:layoutOptions
       #js {"elk.algorithm"                             "layered"
            "elk.direction"                             "RIGHT"
            "elk.edgeRouting"                           "SPLINES"
            "elk.layered.compaction.postCompaction.strategy" "DOWN"
            ;; "elk.layered.mergeHierarchyEdges"           true
            ;; "elk.layered.wrapping.strategy" "MULTI_EDGE"
            "elk.hierarchyHandling" "SEPARATE_CHILDREN"
            ;; "elk.layered.highDegreeNodes.threshold" 5
            ;; "elk.layered.highDegreeNodes.treatment" true
            "elk.contentAlignment" "H_LEFT"
            ;; "layering.layerConstraint" "FIRST"
            ;; "elk.nodeLabels.placement"                  "V_BOTTOM"
            "elk.spacing.labelNode"                     0
            "elk.spacing.nodeNode"                      50
            "elk.spacing.edgeEdge"                      10
            "elk.spacing.componentComponent"            50
            "elk.layered.spacing.nodeNodeBetweenLayers" 500}})


(defn do-layout [data params]
  (-> (.layout elk data layout-options)
      (.then
       (fn [g]
         (let [layout   (js->clj g :keywordize-keys true)
               nodes    (:children layout)
               origin   (:origin params)
               group-id (:group-id params)

               {:keys [jobs]} (utils/query {:jobs [:jobs]})
               jobs'    (reduce (fn [acc {:keys [id x y]}]
                                  (-> acc
                                      (assoc-in [id :x] (+ x (:x origin)))
                                      (assoc-in [id :y] (+ y (:y origin)))))
                                jobs
                                nodes)]

           (re-frame/dispatch [:dag/update-layout-timestamp (:dag-id params)])
           (re-frame/dispatch [:dag/update-jobs jobs'])
           (re-frame/dispatch [:dag/move-group-nodes group-id 0 0]))))))


(re-frame/reg-event-db
 :dag/group-autolayout
 (fn [db [_ dag-id group-id]]
   (let [node-ids    (get-in db [:groups group-id :nodes])
         nodes-set   (set node-ids)

         {:keys [node-width node-height]}
         (utils/query {:node-width  [:dag-params :node-width]
                       :node-height [:dag-params :node-height]})

         {:keys [x y]} (group-rect db dag-id group-id)

         edges       (vals (get db :connections))
         connections (filter (fn [{:keys [start-id end-id]}]
                               (or (nodes-set start-id)
                                   (nodes-set end-id)))
                             edges)

         elk-params  #js {:id       "dag"

                          :children (clj->js (map (fn [node-id]
                                                    {:id     node-id
                                                     :width  node-width
                                                     :height node-height
                                                     :type   "transform"
                                                     :labels [{:text node-id}]})
                                                  node-ids))
                          :edges    (clj->js (map (fn [{:keys [start-id end-id id]}]
                                                    {:id      id
                                                     :sources [start-id]
                                                     :targets [end-id]})
                                                  connections))}]
     (do-layout elk-params {:origin   {:x (+ x 28) :y (+ y 28)}
                            :dag-id   dag-id
                            :group-id group-id})
     nil)))


(defn do-layout-all [data {:keys [callback dag-id]}]
  (-> (.layout elk data layout-options)
      (.then
       (fn [g]
         (let [layout (js->clj g :keywordize-keys true)
               groups (:children layout)
               nodes  (mapcat (fn [{:keys [children x y]}]
                                (map #(assoc % :offset-x x :offset-y y)
                                     children))
                              groups)

               {:keys [jobs]} (utils/query {:jobs [:jobs]})
               jobs'  (reduce (fn [acc {:keys [id x y offset-x offset-y]}]
                                (-> acc
                                    (assoc-in [id :x] (+ x offset-x))
                                    (assoc-in [id :y] (+ y offset-y))))
                              jobs
                              nodes)]

           (re-frame/dispatch-sync [:dag/update-layout-timestamp dag-id])
           (re-frame/dispatch-sync [:dag/update-jobs jobs'])
           (re-frame/dispatch-sync [:dag/update-raw-groups groups])



           ;; Try to do initial fit of the dag

           ;; (let [rect   (utils/calculate-rect nodes)
           ;;       center (:center rect)
           ;;
           ;;       window-width  (.-innerWidth js/window)
           ;;       window-height (.-innerHeight js/window)
           ;;       pixel-ratio   (.-devicePixelRatio js/window)
           ;;
           ;;       window-width'  (/ window-width  pixel-ratio)
           ;;       window-height' (/ window-height pixel-ratio)
           ;;
           ;;       dag-height (:height rect)
           ;;       dag-width  (:width  rect)
           ;;
           ;;       scale (min
           ;;              (*
           ;;               (min (if (< dag-height window-height)
           ;;                      (/ dag-height window-height)
           ;;                      (/ window-height dag-height))
           ;;                    (if (< dag-width window-width)
           ;;                      (/ dag-width window-width)
           ;;                      (/ window-width dag-width))
           ;;                    1)
           ;;               0.6)
           ;;
           ;;              ;; make sure we don't scale closer than the initial
           ;;              ;; scale of 0.6
           ;;              0.5)]
           ;;
           ;;   ;; Add a bit of padding
           ;;   (re-frame/dispatch-sync [:dag/set-position dag-id {:x 50 :y 100}]))

           (doall
            (for [group-id (map :id groups)]
              (re-frame/dispatch [:dag/move-group-nodes group-id 0 0])))

           (re-frame/dispatch [:dag/set-loading false])

           (when callback
             (re-frame/dispatch callback)))))
      (.catch (fn [error]
                (println error)))))


(defn is-transform? [jobs node-id]
  (let [node (get-in jobs [node-id])]
    (= :transform (:type node))))

(defn is-group? [groups node-id]
  (let [node (get-in groups [node-id])]
    (= :group (:type node))))

(re-frame/reg-event-fx
 :dag/full-autolayout
 (fn [{:keys [db]} [_ dag-id params]]
   (let [show-sources?     (get-in db [:dag dag-id :show-sources?])
         show-sources?     (if (some? show-sources?)
                             show-sources?
                             @(re-posh/subscribe [:unleash/is-enabled "fe.manual-dag-layout"]))

         all-groups        (get db :groups)
         all-dag-groups    (get-in db [:dag dag-id :groups])
         dag-groups        (if show-sources?
                             all-dag-groups
                             (set (filter (partial is-group? all-groups) all-dag-groups)))
         groups            (select-keys all-groups dag-groups)


         all-dag-nodes     (set (get-in db [:dag dag-id :nodes]))
         all-jobs          (get db :jobs)
         dag-nodes         (if show-sources?
                             all-dag-nodes
                             (set (filter (partial is-transform? all-jobs) all-dag-nodes)))


         dag-edges         (get-in db [:dag dag-id :edges])
         all-edges         (get db :connections)
         edges             (vals (select-keys all-edges dag-edges))
         edges             (filter (fn only-present [edge]
                                     (and (contains? dag-nodes (:start-id edge))
                                          (contains? dag-nodes (:end-id edge))))
                                   edges)

         {:keys [node-width node-height]} (get db :dag-params)

         node-children     (fn [node-id]
                             {:id     node-id
                              :width  node-width
                              :height node-height
                              :type   "transform"
                              :labels [{:text node-id}]})

         group-connections (distinct (reduce (fn [acc {:keys [start-id end-id]}]
                                               (let [start-group (get-in db [:jobs start-id :group-id])
                                                     end-group   (get-in db [:jobs end-id :group-id])
                                                     edge        {:id      (str start-group ":" end-group)
                                                                  :sources [start-group]
                                                                  :targets [end-group]}]
                                                 (conj acc edge)))
                                             []
                                             edges))


         elk-params        #js {:id    "dag"

                                :children
                                (clj->js (map (fn [{:keys [id nodes]}]
                                                (let [nodes       (filter #(contains? dag-nodes %) nodes)
                                                      nodes-set   (set nodes)
                                                      connections (filter (fn [{:keys [start-id end-id]}]
                                                                            (or (nodes-set start-id)
                                                                                (nodes-set end-id)))
                                                                          edges)]


                                                  {:id       id
                                                   :width    1
                                                   :height   1000
                                                   :type     "group"
                                                   :layoutOptions
                                                   #js {"elk.padding" "[top=85,left=30,bottom=50,right=30]"
                                                        "elk.layered.compaction.postCompaction.strategy" "bottom"}
                                                   :labels   [{:text id}]
                                                   :children (map node-children nodes)
                                                   :edges    (clj->js (map
                                                                       (fn [{:keys [start-id end-id id]}]
                                                                         {:id      id
                                                                          :sources [start-id]
                                                                          :targets [end-id]})
                                                                       connections))}))
                                              (vals groups)))

                                :edges (clj->js group-connections)}]
     (do-layout-all elk-params (assoc params :dag-id dag-id))
     {:db db
      :fx [[:dispatch [:dag/set-loading true "Laying out the graph"]]]})))

(re-frame/reg-event-fx
 :dag/full-layout
 (fn [{:keys [db]} [_ dag-id params]]
   (let [stored-layout (:layout db)]
     (if (or (not= dag-id :default-dag)
             (not stored-layout))

       {:dispatch [:dag/full-autolayout dag-id params]}

       {:db (-> db
                (update :jobs        #(merge-with merge % (select-keys (:jobs stored-layout) (keys %))))
                (update :connections #(merge-with merge % (select-keys (:connections stored-layout) (keys %)))))}))))


(re-frame/reg-event-db
 :dag/set-raw-data
 (fn [db [_ raw-data]]
   (update db :raw-data merge raw-data)))

(defn get-entity-keys [entity]
  (if (contains? entity :transform-id)
    [:transform :group-id]
    [:extract   :source-id]))

(re-frame/reg-event-fx
 :dag/convert-raw-data
 (fn [{:keys [db]} [_ node-id]]
   (let [stored-layout   (:layout db)
         dag-id          :default-dag

         show-all-extracts? (get-in db [:dag dag-id :show-all-extracts?])

         nodes           (get-in db [:raw-data :nodes])
         edges           (get-in db [:raw-data :edges])
         transforms      (get-in db [:raw-data :transforms])

         sources         (get-in db [:raw-data :sources])
         extracts        (get-in db [:raw-data :extracts])

         conns           (set (flatten edges))

         groups          (or (get-in db [:raw-data :parent-groups]) (get-in db [:raw-data :groups]))
         parent-groups   (concat groups sources)
         edges-in-cycles (->> (get-in db [:raw-data :edges-in-cycles])
                              (map (fn [[start-id end-id]] (str start-id ":" end-id)))
                              set)

         groups-map     (reduce (fn [acc group]
                                   (assoc acc (or (get group :id) (get group :source-id)) group))
                                 {}
                                 parent-groups)

         entities        (concat transforms extracts)
         entity-key      :master-id

         jobs            (reduce (fn [acc id]
                                   (let [job              (get-in stored-layout [:jobs id])
                                         entity           (minoro.utils/find-first #(= id (get % entity-key)) entities)
                                         [type group-key] (get-entity-keys entity)
                                         transform?       (= type :transform)
                                         group-id         (get entity group-key "")
                                         ;; We want to display all transforms and extracts with at least one connection
                                         has-connections? (conns (entity-key entity))
                                         default-coord    (if stored-layout nil 0)]

                                     (if (or transform? show-all-extracts? has-connections?)
                                       (assoc acc id {:id        id
                                                      :name      (get entity :name)
                                                      :x         (get job :x default-coord)
                                                      :y         (get job :y default-coord)
                                                      :transform (when transform? entity)
                                                      :extract   (when-not transform? entity)
                                                      :type      type
                                                      :group-id  group-id
                                                      :status    :pending})
                                       acc)))
                                 {}
                                 nodes)


         grouped-jobs (group-by :group-id (vals jobs))
         groups       (into {}
                            (map (fn [[group-id entities]]
                                   (let [type   (if (-> entities first :type (= :transform)) :group :source)
                                         entity (get groups-map group-id)]
                                     [group-id {:name   (:name entity)
                                                :id     group-id
                                                :type   type
                                                :source (when (= type :source) entity)
                                                :group  (when (= type :group) entity)
                                                :nodes  (mapv :id entities)}]))
                                 grouped-jobs))


         ;; Second iteration to find not layed out nodes and place in its group
         ;; or over top left corner
         jobs            (into {} (map (fn [[id node]]
                                         [id
                                          (if (nil? (:y node))
                                            (let [group-node-ids (-> groups (get (:group-id node)) :nodes set (disj id))
                                                  coords         (fn []
                                                                   (->> jobs
                                                                        (keep #(when (group-node-ids (first %))
                                                                                 (second %)))
                                                                        (reduce (fn [acc node]
                                                                                  (if (and (:x node) (:y node)
                                                                                           (or (> (:x acc) (:x node))
                                                                                               (not (:y acc))
                                                                                               (<= (:y acc) (:y node))))
                                                                                    {:x (:x node) :y (+ (:y node) 180)}
                                                                                    acc))
                                                                                {:x nil :y nil})))]
                                              (if (zero? (count group-node-ids))
                                                (update node :y (constantly -380) :x (constantly 0))
                                                (merge node (coords))))
                                            node)])
                                       jobs))


         connections  (reduce (fn [acc [start-id end-id]]
                                (let [id         (str start-id ":" end-id)
                                      connection (get-in stored-layout [:connections id])
                                      in-cycle?  (contains? edges-in-cycles id)]
                                  (assoc acc id {:start     (get connection :start {:x 0 :y 0})
                                                 :end       (get connection :end {:x 0 :y 0})
                                                 :start-id  start-id
                                                 :end-id    end-id
                                                 :in-cycle? in-cycle?
                                                 :type      :default
                                                 :id        id})))
                              {}
                              edges)


         jobs'           (reduce (fn [acc [_ {:keys [id start-id end-id]}]]
                                   (cond-> acc
                                     (some? start-id)
                                     (update-in [start-id :output] conj id)

                                     (some? end-id)
                                     (update-in [end-id :input] conj id)))
                                 jobs
                                 connections)

         job-ids         (vec (keys jobs))
         connection-ids  (vec (keys connections))]

     (cond-> {:db (-> db
                      (assoc-in [:dag dag-id :groups] (vec (keys groups)))
                      (assoc-in [:dag dag-id :nodes] job-ids)
                      (assoc-in [:dag dag-id :edges] connection-ids)
                      (assoc :groups groups
                             :jobs jobs'
                             :connections connections))}

       (nil? stored-layout)
       (assoc :fx [[:dispatch [:dag/full-autolayout dag-id {:callback (when node-id
                                                                        [:dag/navigate-to-node dag-id node-id])}]]])

       (and stored-layout node-id)
       (assoc :fx [[:dispatch [:dag/navigate-to-node dag-id node-id]]])))))


(defn- pick-value-keys
  [coll keys]
  (->> coll
       (map (fn [[id v]] [id (select-keys v keys)]))
       (into {})))

(defn- deep-merge
  [coll1 coll2]
  (merge-with merge coll1 coll2))

(re-frame/reg-event-fx
 :dag/persist-layout
 (fn [{:keys [db]} [_ dag-id new-layout]]
   (let [{:keys [jobs connections]} db
         layout (when new-layout
                  {:jobs        (pick-value-keys (deep-merge jobs (:jobs new-layout))
                                                 [:x :y])
                   :connections (pick-value-keys (deep-merge connections (:connections new-layout))
                                                 [:start :end :start-id :end-id])})]
     {:dispatch-n [[:dag/set-layout-origin nil]
                   [:dag.api/save-layout {:layout       layout
                                          :show-sources (get-in db [:dag dag-id :show-sources?])}]]})))


(re-frame/reg-event-fx
 :dag/reset-layout
 (fn [_ [_ dag-id]]
   {:dispatch-n [[:app.events/add-snack :success "Layout reset"]
                 [:dag/persist-layout dag-id]]}))


(re-frame/reg-event-db
 :dag/open-tooltip
 (fn [db [_ open? & [content]]]
   (if open?
     (assoc db :node-tooltip {:open?   true
                              :content content})
     (assoc db :node-tooltip {:open?   false
                              :content nil}))))

(re-frame/reg-event-db
 :dag/set-layout
 (fn [db [_ layout]]
   (assoc db :layout layout)))


(re-frame/reg-event-db
 :dag/set-layout-origin
 (fn [db [_ origin-id]]
   (assoc db :layout/origin origin-id)))


(re-frame/reg-event-db
 :dag/set-available-dag-layouts
 (fn [db [_ layouts]]
   (assoc db :available-dag-layouts layouts)))

(re-frame/reg-event-fx
 :dag/preview-layout
 (fn [{:keys [db]} [_ dag-id user-id]]
   (let [available-layouts (:available-dag-layouts db)
         layout-data (minoro.utils/find-by-field available-layouts :user-id user-id)
         layout (some-> layout-data :layout edn/read-string)

         {:keys [jobs]} (utils/query {:jobs [:jobs]})
         jobs'    (reduce (fn [acc [id {:keys [x y]}]]
                            (if (some? (get acc id))
                              (-> acc
                                  (assoc-in [id :x] x)
                                  (assoc-in [id :y] y))
                              acc))
                          jobs
                          (:jobs layout))]

     {:dispatch-n [[:dag/set-loading true "Applying Pipelines layout"]
                   [:dag/set-layout layout]
                   [:dag/set-layout-origin user-id]
                   [:dag/update-jobs jobs']
                   [:dag/set-sources-on-dag :default-dag (if (boolean? (:show-sources layout-data))
                                                           (:show-sources layout-data)
                                                           true)]
                   [:dag/update-layout-timestamp dag-id]
                   [:dag/set-loading false]]})))
